import type { AvatarConfig } from '@perplexicon/validation';

import { AvatarRenderer } from './avatar/AvatarRenderer.js';
import { colorForSeed, initialsFor } from './avatar-utils.js';

export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  /** A stable identifier (a user id) the fallback colour is derived from, so the same user always renders the same colour. */
  seed: string;
  /** The display label (typically a username) initials are derived from. */
  label: string;
  size?: AvatarSize;
  /**
   * The user's saved avatar configuration (APP_SPEC §13). When present, the
   * layered character art is drawn; when absent (a user who never
   * customized), a deterministic initials chip is the fallback.
   */
  config?: AvatarConfig | null;
  /**
   * `eager` for avatars in the first paint (the header, a profile header).
   * Defaults to `lazy`, which is what long lists of users want.
   */
  loading?: 'lazy' | 'eager';
}

const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-16 w-16 text-lg',
};

const SIZE_PX: Record<AvatarSize, number> = { sm: 32, md: 40, lg: 64 };

/**
 * A player avatar (APP_SPEC §13; docs/APP_PLAN.md §3 — no image upload).
 * Renders the user's customized layered character art when they have one,
 * otherwise a deterministic initials chip derived from the user id. Purely
 * decorative next to the username it always accompanies, so it is
 * `aria-hidden` rather than announced redundantly (APP_SPEC §36).
 */
export function Avatar({ seed, label, size = 'md', config, loading }: AvatarProps) {
  if (config) {
    return (
      <span
        aria-hidden="true"
        className={`inline-flex shrink-0 select-none overflow-hidden rounded-full border border-brand/50 shadow-1 ${SIZE_CLASSES[size]}`}
      >
        <AvatarRenderer config={config} size={SIZE_PX[size]} loading={loading} />
      </span>
    );
  }

  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 select-none items-center justify-center rounded-full border border-brand/50 font-semibold text-textOnDark shadow-1 ${SIZE_CLASSES[size]}`}
      style={{ backgroundColor: colorForSeed(seed) }}
    >
      {initialsFor(label)}
    </span>
  );
}
